import {  connect } from "react-redux";
import { withRouter} from "react-router-dom";
import { FormControl, Grid, MenuItem, Select } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import LineChartIcon from '../../asset/image/LineChartIcon.svg';
import ColumnChartIcon from '../../asset/image/ColumnChartIcon.svg';
import { getDateForChart, getMinutes, getSingleRecordChartLable, getTimezoneCity } from "../../utils/util";
import moment from "moment";
import { useEffect, useState } from "react";
import _ from "lodash";

const AnalyticsLineChart = (props) => {
    const { userTimezone, newSummaryStartDate, newSummaryEndDate, lineChartData, chartType, setChartType, selectedBin, setSelectedBin, expand, HighchartsInstance, chartRef } = props;

    const timeZone = getTimezoneCity(userTimezone);
    const [hoverStart, setHoverStart] = useState();
    const [titleLeft, setTitleLeft] = useState(35);
    const rightSpace = 140;

    useEffect(() => {
        if (!_.isEmpty(lineChartData)) {
            const lastSeries = lineChartData[lineChartData.length - 1];
            const lastPoint = lastSeries?.data?.[lastSeries?.data?.length - 1];

            if (lastPoint && lastPoint.x !== undefined) {
                setHoverStart(lastPoint.x);
            }
        }
    }, [lineChartData]);

    const colors = ['#2f2282', '#1b51a7', '#3dab97', '#5e3f9e', '#3e88d3', '#1d136a', '#69b8e5', '#3f79ca', '#9e8cc5', '#ed9340',
        '#8cafdf', '#d9914f', '#86c6df', '#3c7df1', '#61cdc0', '#64a1f4', '#dc6161', '#146680', '#2039b5', '#00ad64',
        '#1b4dac', '#b50000', '#8cb5ec', '#c1d5f6', '#b3bbe6', '#e5a8a8', '#f9dabe', '#4b91f1', '#8dbdff', '#efeab9',
        '#8a39a7', '#d9cb4f', '#145f89', '#e7d696', '#8592e7', '#e78585', '#92d7c9', '#aeaeb1', '#755c5d', '#7c6ce6',
        '#cc7460', '#bd9331', '#15e1e8', '#514ced', '#8633de', '#eb52b3', '#c4355b', '#6dc779', '#5373a3', '#8dc293'
    ]

    const formatLargeNumber = (num: number) => {
        if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M';
        if (num >= 1_000) return (num / 1_000).toFixed(2) + 'K';
        return num.toFixed(1);
    };

    const dynamicHeight = window.innerHeight - 450;
    const options = {
        time: {
            timezone: timeZone
        },
        chart: {
            type: chartType,
            height: dynamicHeight,
            zoomType: 'x',
            marginRight: rightSpace,
            plotBorderWidth: 1,
            resetZoomButton: {
                theme: {
                    style: {
                        display: 'none'
                    }
                }
            },
            style: {
                fontFamily: "Roboto, Nunito Sans, Arial, Verdana, Helvetica, sans-serif",
            },
            events: {
                redraw: function () {
                    document.querySelectorAll('.highcharts-data-label').forEach((label:any) => {
                        label.style.left = '0px';
                        label.style.transform = 'none';
                    });
                }
            }
        },
        colors: colors,
        title: {
            text: '',
            align: 'left',
            floating: true,
            x: 12,
            y: 32,
            style: {
                fontWeight: '500'
            }
        },
        lang: {
            noData: "No Data",
        },
        noData: {
            style: {
                fontWeight: 'bold',
                fontSize: '15px',
                color: '#303030',
            },
        },
        credits: {
            enabled: false
        },
        legend: {
            enabled: false
        },
        plotOptions: {
            series: {
                turboThreshold: 1000000,
                stickyTracking: false,
                connectNulls: false,
                dataLabels: {
                    enabled: true,
                    align: 'left',
                    verticalAlign: 'middle',
                    useHTML: true,
                    x: 0,
                    y: 0,
                    inside: false,
                    distance: 30,
                    overflow: 'none',
                    crop: false,
                    formatter: function (this: any) {
                        const chart = this.series.chart;

                        const visibleSeries = chart.series.filter(s => s.visible);
                        const lastVisibleSeries = visibleSeries[visibleSeries.length - 1];
                        if (this.series !== lastVisibleSeries) return null;

                        // Determine which x to use (either clicked or default to last point)
                        const hoverX = hoverStart ??
                            (visibleSeries[0]?.data?.[visibleSeries[0].data.length - 1]?.x ?? this.x);

                        // Render only if this point is the hoverX one
                        if (this.x !== hoverX) return null;
                        if (chart.plotLeft !== titleLeft) {
                            setTitleLeft(chart.plotLeft);
                        }

                        const options: Intl.DateTimeFormatOptions = {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            hour12: false,
                        };
            
                        const formattedDate = new Intl.DateTimeFormat('en-US', {
                            ...options,
                            timeZone: timeZone,
                        }).format(new Date(hoverX));

                        const useFixed = visibleSeries.length > 1;
                        const containerStyle = useFixed
                            ? `
                                position: fixed;
                                top: ${!expand.isCardExpanded ? '285px' : '378px'};
                                right: 16px;
                            `
                            : `
                                position: absolute;
                                top: -20px;
                                left: ${chart.plotWidth - 12}px;
                            `;

                        let html = `
                            <div style="
                                max-height: ${chart.plotHeight}px;
                                overflow-y: auto;
                                display: flex;
                                flex-direction: column;
                                align-items: flex-start;
                                padding: 4px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                ${containerStyle}
                            ">
                                <div style="color: rgb(182 178 178); font-weight: 500; margin-bottom: 4px; margin-left: 20px">
                                    ${formattedDate}
                                </div>
                        `;

                        visibleSeries.forEach((series) => {
                            const lastPoint = series.data[series.data.length - 1];
                            const selectedPoint = series.data.find(point => point.x === hoverStart);
                            const point = selectedPoint || lastPoint;

                            if (point?.x !== hoverX) return;

                            const unit = series.userOptions.yAxisUnit || '';

                            html += `
                                <div style="display: flex; align-items: center; gap: 6px; font-weight: bold; color: #2F4B82; margin-bottom: 1px;">
                                    <div style="width: 8px; height: 5px; background: ${series.color}; margin-left: 6px;"></div>
                                    <span>
                                        ${typeof point.y === 'number' ? formatLargeNumber(point.y) : point.y} ${unit}
                                    </span>
                                    <span 
                                        title="${series.name}" 
                                        style="width: 65px; max-width: 65px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: inline-block;"
                                    >
                                        <b>${series.name}</b>
                                    </span>
                                </div>
                            `;
                        });
                        html += `</div>`;
                        return html;
                    },                    
                    style: {
                        color: '#2F4B82',
                        fontSize: '12px',
                        fontWeight: 'bold'
                    },
                },
                point: {
                    events: {
                        click: function (this: any) {
                            setHoverStart(this.x);
                        },
                    },
                }
            },            
            column: {
                stacking: true
            }
        },
        tooltip: {
            valueSuffix: ``,
            pointFormat: '<span style="color:{series.color}">{series.name}</span>: <b>{point.y} {series.yAxisUnit}</b><br/>',
            valueDecimals: 2,
            useHTML: true,
            borderRadius: 15,
            backgroundColor: '#060606',
            formatter: function (this: any) {
                const options: Intl.DateTimeFormatOptions = {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: false,
                };
    
                const timeZoneFormattedDate = new Intl.DateTimeFormat('en-US', {
                    ...options,
                    timeZone: timeZone,
                }).format(new Date(this.x));
    
                return `
                    <b style="color: #7e8089">${timeZoneFormattedDate}</b><br/>
                    <b style="color: #7e8089">${this.series.name}</b>: 
                    <b style="color: #fff">${this.point.y.toFixed(2)} ${this.series.userOptions.yAxisUnit || ''}</b>
                `;
            }
        },
        xAxis: {
            gridLineWidth: 0.5,
            reversed: false,
            type: "datetime",
            maxPadding: 0.05,
            showLastLabel: true,
            labels: {
                format: getDateForChart(lineChartData) && getSingleRecordChartLable(lineChartData, timeZone),
                style: {
                    color: '#2F4B82',
                    fontFamily: 'Inter',
                    fontSize: '9px',
                    fontWeight: '500',
                }
            },
            plotBands: [{
                color: 'red',
                from: moment(hoverStart),
                to: moment(hoverStart),
            }]
        },
        yAxis: {
            // tickInterval: 1,
            gridLineWidth: 1,
            tickPositioner: function (this: any) {
                if (lineChartData?.[0]?.yAxisLabel === "%") {
                    return this.tickPositions.filter(tick => tick <= 100);
                }
                return this.tickPositions;
            },
            labels: {
                formatter: function (this: any) {
                    const unit = this.chart.series[0]?.userOptions.yAxisUnit || "";
                    return `${this.value} ${unit}`;
                },
                style: {
                    color: '#2F4B82',
                    fontFamily: 'Inter',
                    fontSize: '9px',
                    fontWeight: '500',
                },
            },
            title: {
                text: lineChartData?.[0]?.yAxisLabel
                    ? `${lineChartData[0].yAxisLabel} ${lineChartData[0].yAxisUnit ? `(${lineChartData[0].yAxisUnit})` : ''}`
                    : 'Loading...'
            }
        },
        exporting: {
            enabled: false
        },
        series: lineChartData ? lineChartData : []
    }

    const handleChartType = (type) => {
        setChartType(type);
    }

    return (
        <Grid item xs={7.5} sm={7.5} md={7.5} lg={7.5} className="pie-chart-summary">
            <Grid>
                <Grid container className="new-summary-control-limit title-toggle-btn-div" mb={1.5}>
                    <Grid item md={7} lg={7} className="title-dropdown-pie-chart captive-bar-chart-pagination">
                        <Grid className="captive-line-chart-title" style={{marginLeft: `${titleLeft}px`}}>
                            {lineChartData?.[0]?.['title']}
                        </Grid>
                    </Grid>
                    <Grid item md={5} lg={5} className="analytics-line-chart-interval-icons" style={{paddingRight: `${rightSpace}px`}}>
                        <FormControl variant="standard" className='selectEntry-pie'>
                            <Select
                                labelId="demo-simple-select-standard-label"
                                id="demo-simple-select-standard"
                                value={selectedBin}
                                onChange={(e) => setSelectedBin(e.target.value)}
                            >
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 5 && <MenuItem value={'5 minute'}>5m</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 10 && <MenuItem value={'10 minute'}>10m</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 15 && <MenuItem value={'15 minute'}>15m</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 30 && <MenuItem value={'30 minute'}>30m</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 60 && <MenuItem value={'1 hour'}>1h</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 120 && <MenuItem value={'2 hour'}>2h</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 180 && <MenuItem value={'3 hour'}>3h</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 360 && <MenuItem value={'6 hour'}>6h</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 720 && <MenuItem value={'12 hour'}>12h</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 1440 && <MenuItem value={'1 day'}>1d</MenuItem>}
                                {getMinutes(newSummaryStartDate, newSummaryEndDate) > 10080 && <MenuItem value={'7 day'}>7d</MenuItem>}
                            </Select>
                        </FormControl>
                        <Grid className="analytics-bar-chart-icons">
                            <img src={LineChartIcon} className={chartType == 'spline' ? 'iconColor-select' : 'iconColor-unselect'} onClick={() => handleChartType('spline')} alt="line" />
                            <img src={ColumnChartIcon} className={chartType == 'column' ? 'iconColor-select' : 'iconColor-unselect'} onClick={() => handleChartType('column')} alt="column" />
                        </Grid>
                    </Grid>
                </Grid>
                <HighchartsReact
                    highcharts={HighchartsInstance ?? Highcharts}
                    options={options}
                    ref={chartRef}
                />
            </Grid>
        </Grid>
    )
}

const mapStateToProps = (state) => ({
    userTimezone: state?.authReducer?.userTimezone,
    newSummaryStartDate: state?.authReducer?.newSummaryStartDate,
    newSummaryEndDate: state?.authReducer?.newSummaryEndDate,
});

export default withRouter(
  connect(mapStateToProps, { })(AnalyticsLineChart)
);